import { useOverlay } from "@base/model/hooks/useOverlay";
import { Button } from "@base/ui/Button/Button";
import { Chip } from "@base/ui/Chip";
import { Dialog } from "@base/ui/Dialog";
import { Dropdown } from "@base/ui/Dropdown";
import Skeleton from "@base/ui/Skeleton";
import { updateAutoPay } from "@features/membership/api/autoPay";
import { membershipQueries } from "@features/membership/api/queryFactory";
import { useAddPaymentMethod } from "@features/membership/model/hooks";
import DeletePaymentMethodDialog from "@features/membership/ui/DeletePaymentMethodDialog";
import UnscribeMembershipDialog from "@features/membership/ui/UnscribeMembershipDialog";
import UpgradePlanDialog from "@features/membership/ui/UpgradePlanDialog";
import { mdiClose, mdiCreditCardOutline, mdiDotsVertical } from "@mdi/js";
import Icon from "@mdi/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type SettingsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

function CardItem({
  id,
  last4,
  isMain,
  onDeleteClick,
}: {
  id: string;
  last4: string;
  isMain: boolean;
  onDeleteClick: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate } = useMutation({
    mutationFn: (paymentMethodId: string) => updateAutoPay(paymentMethodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: membershipQueries.paymentMethodsKey() });
      queryClient.invalidateQueries({ queryKey: membershipQueries.autoPayKey() });

      toast.success(t("paymentMethodUpdatedSuccess"));
    },
    onError: () => {
      toast.error(t("paymentMethodUpdateFailed"));
    },
  });

  const handleUseAsMainClick = (id: string) => {
    mutate(id);
  };

  return (
    <div className="flex gap-4 items-center p-4 bg-white border border-gray-500 rounded-4">
      <Icon path={mdiCreditCardOutline} size="1.5rem" className="text-gray-700 shrink-0" />
      <span className="inline-flex flex-1 gap-2 items-center min-w-0 text-body3 text-gray-1000">
        {t("cardLast4", { last4 })}
        {isMain && (
          <Chip variant="fill" size="sm" color="primary" radius="square">
            {t("main")}
          </Chip>
        )}
      </span>

      {!isMain && (
        <Dropdown>
          <Dropdown.Trigger asChild>
            <button type="button" className="p-1 -m-1 hover:bg-gray-100 rounded-4" aria-label={t("settings")}>
              <Icon path={mdiDotsVertical} size="1.25rem" className="text-gray-500" />
            </button>
          </Dropdown.Trigger>
          <Dropdown.Content className="p-1 z-dropdown">
            <Dropdown.Item
              value="use-as-main"
              className="px-3 py-2 cursor-pointer rounded-4 text-body3 text-gray-1000 hover:bg-gray-100"
              onClick={() => handleUseAsMainClick(id)}
            >
              {t("useAsMain")}
            </Dropdown.Item>
            <Dropdown.Item
              value="delete"
              className="px-3 py-2 text-red-500 cursor-pointer rounded-4 text-body3 hover:bg-red-10"
              onClick={onDeleteClick}
            >
              {t("deleteCard")}
            </Dropdown.Item>
          </Dropdown.Content>
        </Dropdown>
      )}
    </div>
  );
}

function SkeletonCardItem() {
  return (
    <div className="flex gap-4 items-center p-4 bg-white border border-gray-500 rounded-4">
      <Skeleton className="w-6 h-6 shrink-0 rounded-4" />
      <Skeleton className="h-4 flex-1 min-w-0 max-w-[120px] rounded-4" />
    </div>
  );
}

const SettingsDialog = ({ isOpen, onClose }: SettingsDialogProps) => {
  const { t } = useTranslation();

  const { open } = useOverlay();

  const { data: membership } = useQuery(membershipQueries.membership());
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } = useQuery({
    ...membershipQueries.paymentMethods(),
    enabled: !!membership,
  });
  const cards = paymentMethods ?? [];

  const { mutate } = useAddPaymentMethod();

  const handleAddCardClick = () => {
    mutate();
  };

  const handleDeleteCardClick = (cardId: string) => {
    open(({ isOpen, close }) => <DeletePaymentMethodDialog isOpen={isOpen} onClose={close} paymentMethodId={cardId} />);
    onClose();
  };

  const handleOpenUnscribeDialog = () => {
    open(({ isOpen, close }) => <UnscribeMembershipDialog isOpen={isOpen} onClose={close} />);
    onClose();
  };

  const handleOpenUpgradePlanDialog = () => {
    open(({ isOpen, close }) => <UpgradePlanDialog isOpen={isOpen} onClose={close} />);
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={onClose}>
        <Dialog.Content className="w-full max-w-[calc(100vw-32px)] md:max-w-[455px]">
          <div className="flex justify-between items-center p-4 pb-0">
            <Dialog.Title>{t("settings")}</Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <button type="button" className="p-1 -m-1 hover:bg-gray-100 rounded-4">
                <Icon path={mdiClose} size="1.5rem" />
              </button>
            </Dialog.CloseTrigger>
          </div>

          <div className="flex flex-col gap-6 p-4">
            <section className="flex flex-col gap-2">
              <h3 className="font-semibold text-label1 text-gray-1000">{t("paymentType")}</h3>
              <div className="flex flex-col gap-2">
                {isPaymentMethodsLoading
                  ? Array.from({ length: 2 }, (_, index) => (
                      <SkeletonCardItem
                        key={`skeleton-card-item-${
                          // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                          index
                        }`}
                      />
                    ))
                  : cards.map((card) => (
                      <CardItem
                        key={card.id}
                        id={card.id}
                        last4={card.last4}
                        isMain={card.is_autopay_method}
                        onDeleteClick={() => handleDeleteCardClick(card.id)}
                      />
                    ))}
              </div>
              <Button type="button" variant="outline" color="secondary" className="w-full" onClick={handleAddCardClick}>
                {t("addCard")}
              </Button>
            </section>

            <div className="flex flex-col gap-2">
              {/* 업그레이드는 Virtual Address → Silver 만 가능 (Day Pass/Silver는 미노출) */}
              {membership && membership.plan_description.type === "VIRTUAL_ADDRESS" && (
                <Button
                  type="button"
                  variant="outline"
                  color="secondary"
                  className="w-full"
                  onClick={handleOpenUpgradePlanDialog}
                >
                  {t("upgradePlan")}
                </Button>
              )}

              <Button
                type="button"
                variant="outline"
                color="danger"
                className="w-full"
                onClick={handleOpenUnscribeDialog}
              >
                {t("unsubscribe")}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog>
    </>
  );
};

export default SettingsDialog;
